import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../services/axiosInstance";
import ReportsTable from '../components/admin/reports/ReportsTable';
import FilterBar from '../components/CommonComponents/FilterBar';
import EmptyState from '../components/CommonComponents/EmptyState';
import { BarChart3, Download, FileText } from 'lucide-react';
import { fetchBranches } from "../services/branchApi";
import { fetchUsers } from "../services/usersApi";

export default function Reports() {
    const user = useSelector(state => state.auth.user);
    const customer_id = user?.customer_id;

    const [deviceTime, setDeviceTime] = useState(() => new Date());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [salesData, setSalesData] = useState([]);
    const [downloading, setDownloading] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    // Filter states
    const [dateFilter, setDateFilter] = useState("all");
    const [branchFilter, setBranchFilter] = useState("");
    const [cashierFilter, setCashierFilter] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [branches, setBranches] = useState([]);
    const [users, setUsers] = useState([]);

    const toLocalStart = (dateStr) => (dateStr ? `${dateStr}T00:00:00` : "");
    const toLocalEnd = (dateStr) => (dateStr ? `${dateStr}T23:59:59.999` : "");

    useEffect(() => {
        const timer = setInterval(() => {
            setDeviceTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Fetch sales report data with date filters
    const fetchReports = async (start = "", end = "", branchId = "", cashierId = "") => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                tzOffset: new Date().getTimezoneOffset(),
            };
            if (start && end) {
                params.startDate = toLocalStart(start);
                params.endDate = toLocalEnd(end);
            }
            if (branchId) params.branch_id = Number(branchId);
            if (cashierId) params.user_id = Number(cashierId);
            console.log('Fetching reports with params:', params);
            const response = await axiosInstance.get("/admin/reports/sales", { params });
            console.log('Response data:', response.data);
            console.log('Number of records:', response.data?.length);
            setSalesData(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Failed to fetch sales report:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    // Fetch branches and users for filters, and initial sales data (today)
    useEffect(() => {
        fetchReports(); // Initial load shows today's data

        if (!customer_id) return;

        fetchBranches(customer_id)
            .then(res => setBranches(res.data.data || []))
            .catch((err) => {
                console.error('Failed to fetch branches:', err);
                setBranches([]);
            });

        fetchUsers()
            .then(res => setUsers(res.data.data || []))
            .catch((err) => {
                console.error('Failed to fetch users:', err);
                setUsers([]);
            });
    }, [customer_id]);

    // Handle date filter submission - applies to both table and export
    const handleDateSubmit = () => {
        fetchReports(startDate, endDate, branchFilter, cashierFilter);
    };

    useEffect(() => {
        fetchReports(startDate, endDate, branchFilter, cashierFilter);
    }, [branchFilter, cashierFilter]);

    const getExportParams = () => {
        const params = { customer_id, tzOffset: new Date().getTimezoneOffset() };
        if (branchFilter) params.branch_id = Number(branchFilter);
        if (cashierFilter) params.user_id = Number(cashierFilter);
        if (startDate) params.startDate = toLocalStart(startDate);
        if (endDate) params.endDate = toLocalEnd(endDate);
        if (!startDate && !endDate && dateFilter) params.dateFilter = dateFilter;
        return params;
    };

    const getExportDateStr = () => (startDate && endDate
        ? `${startDate}_to_${endDate}`
        : dateFilter || 'all');

    // Bulk transaction export handler (CSV)
    const handleBulkExport = async () => {
        setDownloading(true);
        try {
            const token = localStorage.getItem("token") || sessionStorage.getItem("token");
            const params = getExportParams();

            const response = await axiosInstance.get("admin/orders/export", {
                params,
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', `transactions_${getExportDateStr()}.csv`);

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('CSV download failed:', error);
            alert('Failed to download CSV report. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    // Bulk transaction export handler (PDF)
    const handlePdfExport = async () => {
        setDownloadingPdf(true);
        try {
            const token = localStorage.getItem("token") || sessionStorage.getItem("token");
            const params = getExportParams();

            const response = await axiosInstance.get("admin/orders/export/pdf", {
                params,
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', `transactions_${getExportDateStr()}.pdf`);

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('PDF download failed:', error);
            alert('Failed to download PDF report. Please try again.');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const salesWithBranchName = salesData.map(sale => ({ ...sale, branch: sale.branch_name || '-' }));

    // Filter configurations
    const filterConfigs = [
        {
            type: 'react-select',
            label: 'Branch',
            value: (() => {
                if (!branchFilter) return { value: "", label: "All Branches" };
                const found = (branches || []).find(b => String(b.id) === branchFilter);
                return found ? { value: String(found.id), label: found.name } : { value: branchFilter, label: branchFilter };
            })(),
            onChange: (value) => setBranchFilter(value),
            options: [
                { value: "", label: "All Branches" },
                ...((branches || []).map(branch => ({ value: String(branch.id), label: branch.name })))
            ],
            isSearchable: true,
            placeholder: "All Branches",
            minWidth: '180px',
        },
        // {
        //     type: 'select',
        //     label: 'Date',
        //     value: dateFilter,
        //     onChange: setDateFilter,
        //     options: [
        //         { value: "today", label: "Today" },
        //         { value: "week", label: "This Week" },
        //         { value: "month", label: "This Month" },
        //         { value: "all", label: "All" },
        //     ],
        //     minWidth: '120px',
        // },
        // {
        //     type: 'select',
        //     label: 'Status',
        //     value: statusFilter,
        //     onChange: setStatusFilter,
        //     options: [
        //         { value: "", label: "All Status" },
        //         { value: "PAID", label: "Paid" },
        //         { value: "Pending", label: "Pending" },
        //         { value: "Cancelled", label: "Cancelled" },
        //     ],
        //     minWidth: '120px',
        // },
        {
            type: 'react-select',
            label: 'Cashier',
            value: (() => {
                if (!cashierFilter) return { value: "", label: "All Cashiers" };
                const found = (users || []).find(u => String(u.id) === cashierFilter);
                return found ? { value: String(found.id), label: found.username || found.email } : { value: cashierFilter, label: cashierFilter };
            })(),
            onChange: (value) => setCashierFilter(value),
            options: [
                { value: "", label: "All Cashiers" },
                ...((users || []).map(user => ({
                    value: String(user.id),
                    label: user.username || user.email
                })))
            ],
            isSearchable: true,
            placeholder: "All Cashiers",
            minWidth: '180px',
        },
    ];

    return (
        <div className="min-h-screen bg-[#F8F9FA] p-4 md:p-8">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
                <p className="text-sm text-gray-600 mt-2">View sales summary and export detailed transaction reports</p>
                <div className="text-sm text-gray-600 mt-1">
                    Device time: <span className="font-semibold text-gray-800">{deviceTime.toLocaleString()}</span>
                </div>
            </div>

            {/* Date Range Filter - Controls both table and export */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Date Filter</h2>
                <div className="flex items-center gap-3 bg-[#f6f6f8] p-4 rounded-xl">
                    <label className="text-sm font-semibold text-gray-700">Select Date Range:</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                            setStartDate(e.target.value);
                            setDateFilter("");
                        }}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
                    />
                    <span className="text-gray-500">to</span>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                            setEndDate(e.target.value);
                            setDateFilter("");
                        }}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
                    />
                    {(startDate && endDate) && (
                        <button
                            onClick={handleDateSubmit}
                            className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                        >
                            Submit
                        </button>
                    )}
                    {(startDate || endDate) && (
                        <button
                            onClick={() => {
                                setStartDate("");
                                setEndDate("");
                                setDateFilter("all");
                                fetchReports(); // Reset to today's data
                            }}
                            className="text-sm font-semibold text-primary hover:text-primary-dark"
                        >
                            Clear
                        </button>
                    )}
                    {!startDate && !endDate && (
                        <span className="text-sm text-gray-500 italic">Showing today's data</span>
                    )}
                </div>
            </div>

            {/* Export Section */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Export Transactions</h2>
                        <p className="text-sm text-gray-600">Apply additional filters and download transaction report as CSV or PDF</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleBulkExport}
                            disabled={downloading || !startDate || !endDate}
                            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold"
                        >
                            <Download size={20} />
                            {downloading ? 'Downloading...' : 'Download CSV'}
                        </button>
                        <button
                            onClick={handlePdfExport}
                            disabled={downloadingPdf || !startDate || !endDate}
                            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold"
                        >
                            <FileText size={20} />
                            {downloadingPdf ? 'Downloading...' : 'Download PDF'}
                        </button>
                    </div>
                </div>

                {/* Export Filters */}
                <FilterBar filters={filterConfigs} />
            </div>

            {/* Sales Summary Table */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-6 border-b">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Sales Summary</h2>
                            <p className="text-sm text-gray-600">Aggregated sales data by date, branch, and cashier</p>
                        </div>
                        {!loading && !error && (
                            <div className="text-sm font-semibold text-gray-700">
                                Total Records: <span className="text-primary">{salesData.length}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="p-6">
                    {error && (
                        <EmptyState
                            icon={BarChart3}
                            title="Failed to Load Reports"
                            message="Check your internet connection and try again."
                            onRetry={fetchReports}
                            retryLabel="Reload"
                        />
                    )}
                    {!error && (
                        <ReportsTable
                            sales={salesWithBranchName}
                            loading={loading}
                            error={error}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
